import React, { useState, useEffect, useCallback } from 'react'
import { Image, RefreshControl } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { ArrowLeft, Package, Clock, CheckCircle, XCircle, ChevronRight, MapPin, X } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { RootStackParamList } from '../types/navigation'
import {
  Button, Sheet, Spinner, Text, XStack, YStack, ScrollView, Divider,
} from '@fliisi/pollen'

interface OfferItem {
  id: string; title: string; description: string | null; price: number
  images: { id: string; image_url: string; sort_order: number }[]
}

interface Offer {
  id: string; store_id: string
  status: 'pending' | 'accepted' | 'rejected' | 'completed'
  total_price: number; description: string | null; seller_notes: string | null
  store_response: string | null; responded_at: string | null; created_at: string
  store: { name: string; city: string | null; offer_dropoff_info: string | null }
  items: OfferItem[]
}

export default function MyOffersScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { t } = useLanguage()
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)

  const fetchOffers = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const { data: offersData, error: offersError } = await supabase
        .from('seller_offers')
        .select(`id, store_id, status, total_price, description, seller_notes, store_response, responded_at, created_at, flea_markets!inner (name, city, offer_dropoff_info)`)
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false })
      if (offersError) throw offersError

      const offersWithItems: Offer[] = []
      for (const offer of offersData || []) {
        const { data: items } = await supabase
          .from('offer_items')
          .select(`id, title, description, price, offer_images (id, image_url, sort_order)`)
          .eq('offer_id', offer.id)
          .order('created_at', { ascending: true })
        offersWithItems.push({
          id: offer.id, store_id: offer.store_id, status: offer.status,
          total_price: offer.total_price, description: offer.description,
          seller_notes: offer.seller_notes, store_response: offer.store_response,
          responded_at: offer.responded_at, created_at: offer.created_at,
          store: { name: (offer.flea_markets as any).name, city: (offer.flea_markets as any).city, offer_dropoff_info: (offer.flea_markets as any).offer_dropoff_info },
          items: (items || []).map(item => ({ id: item.id, title: item.title, description: item.description, price: item.price, images: (item.offer_images as any[]) || [] })),
        })
      }
      setOffers(offersWithItems)
    } catch (error) {
      console.error('Error fetching offers:', error)
    } finally {
      setLoading(false); setRefreshing(false)
    }
  }, [])

  useEffect(() => { fetchOffers() }, [fetchOffers])
  const onRefresh = () => { setRefreshing(true); fetchOffers() }

  const getStatusIcon = (status: Offer['status']) => {
    const map = { pending: <Clock size={20} color="#f59e0b" />, accepted: <CheckCircle size={20} color="#22c55e" />, rejected: <XCircle size={20} color="#ef4444" />, completed: <CheckCircle size={20} color="#3b82f6" /> }
    return map[status]
  }

  const getStatusText = (status: Offer['status']) => {
    const map = { pending: t('offer_pending') || 'Odottaa vastausta', accepted: t('offer_accepted') || 'Hyväksytty', rejected: t('offer_rejected') || 'Hylätty', completed: t('offer_completed') || 'Valmis' }
    return map[status]
  }

  const getStatusColor = (status: Offer['status']) => {
    const map = { pending: '#fef3c7', accepted: '#dcfce7', rejected: '#fee2e2', completed: '#dbeafe' }
    return map[status]
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' })

  const HeaderBar = () => (
    <XStack ai="center" jc="space-between" px="$md" py="$md" bg="#ffffff" bbw={1} bbc="#e5e7eb">
      <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}>
        <ArrowLeft size={20} color="#748C70" />
      </Button>
      <Text fontSize={18} fontWeight="600" color="#111827">{t('my_offers') || 'Omat tarjoukset'}</Text>
      <YStack w={40} />
    </XStack>
  )

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#f9fafb' }}>
        <HeaderBar />
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#f9fafb' }}>
      <HeaderBar />

      {offers.length === 0 ? (
        <YStack f={1} jc="center" ai="center" p="$2xl">
          <Package size={64} color="#d1d5db" />
          <Text fontSize={18} fontWeight="600" color="#374151" mt="$md">{t('no_offers_yet') || 'Ei vielä tarjouksia'}</Text>
          <Text fontSize={14} color="#6b7280" textAlign="center" mt="$sm">
            {t('no_offers_description') || 'Tarjouksesi näkyvät täällä kun olet lähettänyt niitä.'}
          </Text>
        </YStack>
      ) : (
        <ScrollView
          f={1} contentContainerStyle={{ padding: 16 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {offers.map((offer) => {
            const firstImage = offer.items[0]?.images[0]?.image_url
            return (
              <YStack
                key={offer.id} bg="#ffffff" br="$lg" p="$md" mb="$md" elevation={2}
                onPress={() => { setSelectedOffer(offer); setDetailModalVisible(true) }}
                pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
              >
                <XStack ai="center">
                  <YStack w={56} h={56} br="$md" overflow="hidden">
                    {firstImage ? (
                      <Image source={{ uri: firstImage }} style={{ width: '100%', height: '100%' }} />
                    ) : (
                      <YStack w="100%" h="100%" bg="#f3f4f6" jc="center" ai="center">
                        <Package size={24} color="#9ca3af" />
                      </YStack>
                    )}
                  </YStack>
                  <YStack f={1} ml="$md">
                    <Text fontSize={16} fontWeight="600" color="#111827">{offer.store.name}</Text>
                    {offer.store.city && (
                      <XStack ai="center" mt={2}>
                        <MapPin size={12} color="#6b7280" />
                        <Text fontSize={12} color="#6b7280" ml={4}>{offer.store.city}</Text>
                      </XStack>
                    )}
                    <Text fontSize={13} color="#6b7280" mt={2}>
                      {offer.items.length} {offer.items.length === 1 ? (t('item_count_singular') || 'tuote') : (t('items_count_plural') || 'tuotetta')}
                    </Text>
                  </YStack>
                  <YStack ai="flex-end">
                    <Text fontSize={16} fontWeight="700" color="#111827" mb={4}>{offer.total_price.toFixed(2)} €</Text>
                    <ChevronRight size={20} color="#9ca3af" />
                  </YStack>
                </XStack>

                <XStack jc="space-between" ai="center" mt="$md" pt="$md" btw={1} btc="#f3f4f6">
                  <XStack ai="center" px={10} py={4} br="$xl" gap="$sm" bg={getStatusColor(offer.status)}>
                    {getStatusIcon(offer.status)}
                    <Text fontSize={13} fontWeight="500" color="#374151">{getStatusText(offer.status)}</Text>
                  </XStack>
                  <Text fontSize={12} color="#9ca3af">{formatDate(offer.created_at)}</Text>
                </XStack>
              </YStack>
            )
          })}
        </ScrollView>
      )}

      {/* Detail Modal */}
      {selectedOffer && (
        <Sheet open={detailModalVisible} onOpenChange={setDetailModalVisible} noHandle>
            <XStack ai="center" jc="space-between" px="$md" py="$md" bbw={1} bbc="#e5e7eb">
              <YStack p={8} onPress={() => setDetailModalVisible(false)} pressStyle={{ opacity: 0.8 }} animation="quick" cursor="pointer">
                <X size={24} color="#111827" />
              </YStack>
              <Text fontSize={18} fontWeight="600" color="#111827">{t('offer_details') || 'Tarjouksen tiedot'}</Text>
              <YStack w={40} />
            </XStack>

            <ScrollView f={1}>
              {/* Store info */}
              <YStack p="$md" bbw={1} bbc="#f3f4f6">
                <Text fontSize={12} fontWeight="600" color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb="$sm">{t('store') || 'Kauppa'}</Text>
                <Text fontSize={18} fontWeight="600" color="#111827">{selectedOffer.store.name}</Text>
                {selectedOffer.store.city && (
                  <XStack ai="center" mt={2}><MapPin size={14} color="#6b7280" /><Text fontSize={14} color="#6b7280" ml={4}>{selectedOffer.store.city}</Text></XStack>
                )}
              </YStack>

              {/* Status */}
              <YStack p="$md" bbw={1} bbc="#f3f4f6">
                <Text fontSize={12} fontWeight="600" color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb="$sm">{t('status') || 'Tila'}</Text>
                <XStack ai="center" als="flex-start" px="$md" py="$sm" br={20} gap="$sm" bg={getStatusColor(selectedOffer.status)}>
                  {getStatusIcon(selectedOffer.status)}
                  <Text fontSize={15} fontWeight="600" color="#374151">{getStatusText(selectedOffer.status)}</Text>
                </XStack>
                {selectedOffer.responded_at && (
                  <Text fontSize={13} color="#6b7280" mt="$sm">{t('responded') || 'Vastattu'}: {formatDate(selectedOffer.responded_at)}</Text>
                )}
              </YStack>

              {/* Store response */}
              {selectedOffer.store_response && (
                <YStack p="$md" bbw={1} bbc="#f3f4f6">
                  <Text fontSize={12} fontWeight="600" color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb="$sm">{t('store_response') || 'Kaupan viesti'}</Text>
                  <YStack bg="#f3f4f6" br="$md" p="$md">
                    <Text fontSize={14} color="#374151" lineHeight={20}>{selectedOffer.store_response}</Text>
                  </YStack>
                </YStack>
              )}

              {/* Dropoff info */}
              {selectedOffer.status === 'accepted' && selectedOffer.store.offer_dropoff_info && (
                <YStack p="$md" bbw={1} bbc="#f3f4f6">
                  <Text fontSize={12} fontWeight="600" color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb="$sm">{t('dropoff_info') || 'Tuontiohjeet'}</Text>
                  <YStack bg="#dcfce7" br="$md" p="$md" blw={4} blc="#22c55e">
                    <Text fontSize={14} color="#166534" lineHeight={20}>{selectedOffer.store.offer_dropoff_info}</Text>
                  </YStack>
                </YStack>
              )}

              {/* Items */}
              <YStack p="$md" bbw={1} bbc="#f3f4f6">
                <Text fontSize={12} fontWeight="600" color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb="$sm">{t('items') || 'Tuotteet'}</Text>
                {selectedOffer.items.map((item, index) => (
                  <YStack key={item.id} bg="#f9fafb" br="$md" p="$md" mb="$sm">
                    <XStack ai="center">
                      <Text fontSize={14} fontWeight="600" color="#6b7280" mr="$sm">{index + 1}.</Text>
                      <Text f={1} fontSize={15} fontWeight="500" color="#111827">{item.title}</Text>
                      <Text fontSize={15} fontWeight="600" color="#111827">{item.price.toFixed(2)} €</Text>
                    </XStack>
                    {item.description && <Text fontSize={13} color="#6b7280" mt="$sm" ml="$lg">{item.description}</Text>}
                    {item.images.length > 0 && (
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8, marginLeft: 20 }}>
                        {item.images.sort((a, b) => a.sort_order - b.sort_order).map(img => (
                          <Image key={img.id} source={{ uri: img.image_url }} style={{ width: 60, height: 60, borderRadius: 6, marginRight: 8 }} />
                        ))}
                      </ScrollView>
                    )}
                  </YStack>
                ))}
              </YStack>

              {/* Total */}
              <XStack jc="space-between" ai="center" p="$md" bg="#f3f4f6">
                <Text fontSize={16} fontWeight="600" color="#374151">{t('total') || 'Yhteensä'}</Text>
                <Text fontSize={20} fontWeight="700" color="#111827">{selectedOffer.total_price.toFixed(2)} €</Text>
              </XStack>

              {/* Seller notes */}
              {selectedOffer.seller_notes && (
                <YStack p="$md" bbw={1} bbc="#f3f4f6">
                  <Text fontSize={12} fontWeight="600" color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb="$sm">{t('your_notes') || 'Omat muistiinpanot'}</Text>
                  <Text fontSize={14} color="#374151" lineHeight={20}>{selectedOffer.seller_notes}</Text>
                </YStack>
              )}

              <YStack h={32} />
            </ScrollView>
        </Sheet>
      )}
    </SafeAreaView>
  )
}
